import React from 'react';
import {useDispatch} from 'react-redux';
import {WeatherWarningListResponse} from '../api/types';
import {pushStationWeatherWarnings} from '../store/weatherData';
import {getStationWeatherWarnings} from '../store/weatherData/selectors';
import useParamSelector from './useParamSelector';
import useWeatherWarningsSync, {
  WeatherWarningsOptions,
} from './useWeatherWarningsSync';

/**
 * Official weather warnings at a station's coordinates, cached in the store per
 * station. `updateStationWarnings` fetches (once at a time, silently);
 * `isWarningUpdateRequired` says whether the stored response is stale; pass
 * `{autoRefresh: true}` to let the hook do both on a timer.
 */
const useStationWarning = (
  stationId: number,
  lat: number,
  lon: number,
  {autoRefresh = false}: WeatherWarningsOptions = {},
) => {
  const dispatch = useDispatch();

  const warningsData: WeatherWarningListResponse | null = useParamSelector(
    getStationWeatherWarnings,
    stationId,
  );

  const onData = React.useCallback(
    (warningListResponse: WeatherWarningListResponse) => {
      dispatch(pushStationWeatherWarnings({stationId, warningListResponse}));
    },
    [dispatch, stationId],
  );

  const {update, isUpdateRequired, isUpdating, hasError, computedAt} =
    useWeatherWarningsSync({
      storeKey: stationId,
      lat,
      lon,
      data: warningsData,
      onData,
      autoRefresh,
    });

  const warnings = warningsData?.warnings;

  return React.useMemo(
    () => ({
      updateStationWarnings: update,
      warnings: warnings || [],
      isWarningUpdateRequired: isUpdateRequired,
      isUpdating,
      /** The last fetch for this place failed (see `useWeatherWarningsSync`). */
      hasError,
      /** The stored response's `computed_at` (epoch seconds), else null. */
      computedAt,
    }),
    [update, warnings, isUpdateRequired, isUpdating, hasError, computedAt],
  );
};

export default useStationWarning;
